import React, { useEffect, useState } from "react";
import { Activity, LineChart, Plus } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import DetailSheet from "../session/generate/DetailSheet";
import HelpLink from "../help/HelpLink";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field, ItemRow, Tag } from "./ProfileParts";
import {
  benchmarkDetail,
  benchmarkFromForm,
  benchmarkFigure,
  groupBenchmarks,
} from "./profileSummaries";
import { EMPTY_FORM, formForSave, formFromCap, newForm, withType } from "./benchmarkForm";
import BenchmarkEntryForm from "./BenchmarkEntryForm";
import { todayIso } from "../../utils/benchmarkNormalise";
import { benchmarkGroups, describeGroup } from "../../utils/capabilityCurves";
import CurvesView, { CurvePicker } from "./curves/CurvesView";

/**
 * Your benchmarks (issue #86): a list grouped by kind with search, and each
 * entry's form in the same sheet. Edits save when you go back or close, so
 * each change is one write; a new entry saves with Add.
 */
const BenchmarksSheet = ({ open, startNew = false, onClose }) => {
  const { isAuthenticated } = useAuth();
  const {
    performanceCapabilities = [],
    addPerformanceCapability,
    updatePerformanceCapability,
    deletePerformanceCapability,
  } = useUserProfile();
  const caps = isAuthenticated ? performanceCapabilities : [];

  // null = the list; "new" or an id = that entry's form; "curves" = Your
  // curves for `curveKey`, and "curves-pick" its exercise picker (#91).
  const [editing, setEditing] = useState(null);
  const [curveKey, setCurveKey] = useState(null);
  // Where Back goes from an entry opened from Your curves.
  const [returnTo, setReturnTo] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) {
      setQuery("");
      setForm(EMPTY_FORM);
      setDirty(false);
      setError(null);
      setReturnTo(null);
      setEditing(startNew ? "new" : null);
    }
    // Only on opening; startNew is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isCurves = editing === "curves" || editing === "curves-pick";
  const openCurves = (key) => {
    setCurveKey(key);
    setReturnTo(null);
    setEditing("curves");
  };

  const openEntry = (id, from = null) => {
    setReturnTo(from);
    const cap = caps.find((c) => c.id === id);
    setForm(cap ? formFromCap(cap) : newForm(todayIso()));
    setEditing(cap ? id : "new");
    setDirty(false);
    setError(null);
    setConfirming(false);
  };
  const setType = (type) => {
    setForm((f) => withType(f, type));
    setDirty(true);
    setError(null);
  };
  const set = (fields) => {
    setForm((f) => ({ ...f, ...fields }));
    setDirty(true);
    setError(null);
  };

  // Leaving an existing entry saves it. Returns false (and stays) if invalid.
  const commit = async () => {
    if (editing == null || editing === "new" || isCurves || !dirty) return true;
    const result = benchmarkFromForm(formForSave(form));
    if (result.error) {
      setError(result.error);
      return false;
    }
    setBusy(true);
    try {
      await updatePerformanceCapability(editing, result.data);
      return true;
    } catch (err) {
      console.error("Failed to save benchmark:", err);
      setError("Couldn't save. Check your connection and try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const back = async () => {
    if (editing === "curves-pick") setEditing("curves");
    else if (await commit()) {
      setEditing(isCurves ? null : returnTo);
      setReturnTo(null);
    }
  };
  const close = async () => {
    if (await commit()) onClose();
  };

  const add = async () => {
    const result = benchmarkFromForm(formForSave(form));
    if (result.error) {
      setError(result.error);
      return;
    }
    setBusy(true);
    try {
      await addPerformanceCapability(result.data);
      setEditing(null);
    } catch (err) {
      console.error("Failed to add benchmark:", err);
      setError("Couldn't save. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deletePerformanceCapability(editing);
      setEditing(returnTo);
      setReturnTo(null);
    } catch (err) {
      console.error("Failed to delete benchmark:", err);
      setError("Couldn't delete. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  let title = "Benchmarks";
  let description =
    "Recent results and how hard you went. Khronos turns each into an estimated 1RM or a pace, and the AI sizes loads and paces from them. A main lift in kg, or seconds per rep, also sets the Coach's rep timing.";
  let footer;
  let body;

  if (editing == null) {
    const groups = groupBenchmarks(caps, query);
    // One curve per exercise, each opening Your curves (#91).
    const curveGroups = query.trim() ? [] : benchmarkGroups(caps);
    body = !isAuthenticated ? (
      <p className="text-sm leading-snug text-muted-foreground">Sign in to save your benchmarks.</p>
    ) : (
      <>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          {caps.length > 0 ? (
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${caps.length} benchmark${caps.length === 1 ? "" : "s"}`}
              aria-label="Search benchmarks"
            />
          ) : (
            <p className="self-center text-sm text-muted-foreground">None yet.</p>
          )}
          <Button type="button" variant="outline" onClick={() => openEntry(null)}>
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
        </div>
        {curveGroups.length > 0 && (
          <Field label="Your curves" aside={String(curveGroups.length)}>
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
              {curveGroups.map((g) => {
                const { figure, detail } = describeGroup(g);
                return (
                  <ItemRow
                    key={g.key}
                    Icon={LineChart}
                    title={g.label}
                    detail={figure || detail}
                    onClick={() => openCurves(g.key)}
                  />
                );
              })}
            </div>
          </Field>
        )}
        {groups.map((g) => (
          <Field key={g.key} label={g.label} aside={String(g.items.length)}>
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
              {g.items.map((c) => {
                const figure = benchmarkFigure(c);
                return (
                  <ItemRow
                    key={c.id}
                    Icon={Activity}
                    title={c.label}
                    detail={benchmarkDetail(c)}
                    tag={figure && <Tag>{figure}</Tag>}
                    onClick={() => openEntry(c.id)}
                  />
                );
              })}
            </div>
          </Field>
        ))}
        {caps.length > 0 && groups.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing matches “{query}”.</p>
        )}
        <HelpLink topic="account-sync#benchmarks" label="How benchmarks are worked out" />
      </>
    );
  } else if (editing === "curves") {
    title = "Your curves";
    description = "What your results add up to.";
    body = (
      <CurvesView
        caps={caps}
        groupKey={curveKey}
        onPick={() => setEditing("curves-pick")}
        onOpenEntry={(id) => openEntry(id, "curves")}
      />
    );
  } else if (editing === "curves-pick") {
    title = "Choose an exercise";
    description = null;
    body = (
      <CurvePicker
        caps={caps}
        selected={curveKey}
        onSelect={(key) => {
          setCurveKey(key);
          setEditing("curves");
        }}
      />
    );
  } else {
    const isNew = editing === "new";
    title = isNew ? "Add a benchmark" : form.label || "Benchmark";
    description = "";
    if (isNew) {
      footer = (
        <Button type="button" className="h-12 w-full text-base font-semibold" onClick={add} disabled={busy}>
          Add
        </Button>
      );
    }
    body = (
      <BenchmarkEntryForm
        key={editing}
        form={form}
        set={set}
        setType={setType}
        isNew={isNew}
        saved={isNew ? null : caps.find((c) => c.id === editing)}
        error={error}
        confirming={confirming}
        onAskDelete={() => setConfirming(true)}
        onCancelDelete={() => setConfirming(false)}
        onDelete={remove}
        busy={busy}
      />
    );
  }

  return (
    <DetailSheet
      open={open}
      onOpenChange={(next) => !next && close()}
      title={title}
      description={description}
      showDescription={Boolean(description)}
      onBack={editing != null ? back : undefined}
      footer={footer}
    >
      {body}
    </DetailSheet>
  );
};

export default BenchmarksSheet;
